import React from 'react';
import ReactDOM from 'react-dom';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { itemCreateTemplate } from 'logic/itemReducers';
import { itemCreateCrafted, getRunewordSockets, isValidRunewordBase } from 'components/equipment/itemCategories';
import { D2ItemTooltip, D2SkillTooltip, D2Tooltip } from 'tooltips';
import { craftingRecipes, isSubType, itemTypeNames, presetItems } from 'logic/itemTypes';
import { loadProfile, renderProfile } from './cache';
import { itemGetColor, itemGetIcon, itemGetName, itemGetTint, statsToLines } from 'logic/item';
import { SkillIcon } from 'components/calculator/Skill';
import { Color, Quality } from 'common';
import { replaceReact } from 'utils/format';
import { searchRegex } from 'utils';
import shrineBuffs from 'logic/skills/shrines';
import GameTooltip from 'tooltips/GameTooltip';
import GameContext from 'gamectx';

const _L = Locale();

const canonicalName = name => name.replace(/.*\n/g, "").replace(/[^\w]/g, "").toLowerCase();

const itemBases = {};
const itemUniques = {};
const itemPreset = {};
const skills = {};
const shrines = {};
const itemRuneword = {};

let _init = false;
function initLookup() {
  if (_init) return;
  _init = true;
  for (const [id, item] of Object.entries(Data.items)) {
    itemBases[canonicalName(Locale.strings[item.namestr] || item.name)] = id;
  }
  for (const [id, item] of Object.entries(Data.uniqueItems)) {
    itemUniques[canonicalName(Locale.strings[item.index] || item.index)] = id;
  }
  for (const [id, item] of Object.entries(Data.setItems)) {
    itemUniques[canonicalName(Locale.strings[item.index] || item.index)] = id;
  }
  for (const [id, item] of Object.entries(presetItems)) {
    itemPreset[canonicalName(itemGetName(item))] = id;
  }
  for (const [id, rune] of Object.entries(Data.runes)) {
    itemRuneword[canonicalName(Locale.strings[rune.name])] = id;
  }
  const skillsChar = {};
  for (const [id, skill] of Object.entries(Data.skills)) {
    const desc = Data.skillDesc[skill.skilldesc];
    if (!desc) continue;
    const name = canonicalName(Locale.strings[desc.strname]);
    if (skill.charclass) {
      if (!skillsChar[name]) skillsChar[name] = id;      
    } else {
      if (!skills[name]) skills[name] = id;
    }
  }
  Object.assign(skills, skillsChar);
  for (const [id, shrine] of Object.entries(shrineBuffs)) {
    const name = canonicalName(Locale.strings[shrine.name]);
    shrines[name] = id;
  }
}

function parseRecipe(text) {
  for (const [id, recipe] of Object.entries(Data.crafted)) {
    const desc = recipe.description;
    if (text.substring(0, desc.length) !== desc) continue;
    const base = itemBases[text.substring(desc.length)];
    if (!base) continue;
    const input = recipe.input1.split(',');
    if (input[2] === 'upg') {
      const item = Data.items[input[0]];
      if (!item) continue;
      if (![item.normcode, item.ubercode, item.ultracode].includes(base)) continue;
    } else {
      if (!isSubType(Data.items[base].type, input[0])) continue;
    }
    return [id, base];
  }
}

function parseRuneword(text) {
  const stype = {shield: 'shld', armor: 'tors', helmet: 'helm', weapon: 'weap'};
  for (const suffix of ['', 'shield', 'armor', 'helmet', 'weapon']) {
    if (text.length <= suffix.length || text.substring(text.length - suffix.length) !== suffix) continue;
    const id = itemRuneword[text.substring(0, text.length - suffix.length)];
    if (!id) continue;
    return [id, stype[suffix]];
  }
}

const IconSize = 24, IconSizeMin = 16;

function itemGetGraphics(item, type, hd) {
  if (item.base || item.quality !== Quality.RUNEWORD) {
    return [itemGetIcon(item, hd), itemGetTint(item)];
  }
  const sockets = getRunewordSockets(item.unique);
  const base = Object.keys(Data.items).find(id => (!type || isSubType(Data.items[id].type, type)) && isValidRunewordBase(item.unique, id, sockets));
  if (!base) return [null, 0];
  return [itemGetIcon({base}, hd), 0]
}

function ItemIcon({ item, type }) {
  const gfx = React.useContext(GameContext).gfx;
  const [rendered, setRendered] = React.useState(false);
  const [icon, tint] = itemGetGraphics(item, type, gfx.hd);
  const ethereal = item.ethereal || item.stats?.item_ethereal;

  const ref = React.useRef();
  React.useEffect(() => gfx.createIcon(icon, tint).then(image => {
    const frame = image?.frames[0];
    if (!frame || !ref.current) return;
    const ctx = ref.current.getContext('2d');

    const scale = Math.min(IconSizeMin / Math.min(frame.width, frame.height), IconSize / Math.max(frame.width, frame.height));
    const width = scale * frame.width;
    const height = scale * frame.height;
    const fwidth = Math.ceil(width);
    ref.current.width = fwidth;
    ref.current.setAttribute('data-width', fwidth);
    ctx.globalAlpha = (ethereal ? 0.5 : 1);
    ctx.drawImage(image.bitmap, frame.x, frame.y, frame.width, frame.height, (fwidth - width) / 2, (IconSize - height) / 2, width, height);
    ctx.globalAlpha = 1.0;
    setRendered(true);
  }), [icon, tint, ethereal, gfx]);

  const style = {};
  if (!rendered) style.display = 'none';
  return <canvas className="d2p-EmbedIcon" style={style} width={IconSize} height={IconSize} ref={ref}/>;
}

export function ItemNode({ item, text, type }) {
  if (item.quality === Quality.RUNEWORD && typeof text === 'string') {
    const base = Data.items[item.base];
    if (base) {
      let basename = (Locale.strings[base.namestr] || base.name).split('\n');
      basename = basename[basename.length - 1];
      text = replaceReact(text, searchRegex(basename), () => <span className={`d2-color-${Color.GRAY}`}>{basename}</span>);
    }
  }
  return (
    <D2ItemTooltip item={item}>
      <span className={`d2p-no-break d2-color-${itemGetColor(item)}`}><ItemIcon item={item} type={type}/> {text}</span>
    </D2ItemTooltip>
  );
}

function SkillNode({ id, level, text }) {
  const skill = Data.skills[id];
  const desc = Data.skillDesc[skill?.skilldesc];
  if (!desc) return null;
  return (
    <D2SkillTooltip id={id} level={level} full canLevel="special">
      <span className="d2p-no-break"><SkillIcon id={id} size={18}/> {text || Locale.strings[desc.strname]}</span>
    </D2SkillTooltip>
  );
}

function ShrineNode({ id, text }) {
  const shrine = shrineBuffs[id];
  if (!shrine) return null;
  const lines = [];
  lines.push(`\\xffc${Color.GOLD}${Locale.strings[shrine.name]}`);
  if (shrine.stats) {
    for (const [, line] of statsToLines({...shrine.stats})) {
      lines.push(`\\xffc${Color.BLUE}${line}`);
    }
  }
  return (
    <D2Tooltip tooltip={<GameTooltip text={lines}/>}>
      <span className="d2p-no-break"><span className={`d2p-ShrineIcon d2p-${id}`}/> {text || Locale.strings[shrine.name]}</span>
    </D2Tooltip>
  );
}

function itemBaseNode(text, id) {
  const item = Data.items[id];
  if (!item) return null;
  return <ItemNode text={text || itemGetName({base: id, quality: Quality.NORMAL})} item={itemCreateTemplate(id)}/>;
}
function itemUniqueNode(text, id) {
  const item = Data.uniqueItems[id] || Data.setItems[id];
  if (!item) return null;
  return <ItemNode text={text || Locale.strings[item.index] || item.index} item={itemCreateTemplate(id)}/>;
}
function itemRunewordNode(text, id, type) {
  const rune = Data.runes[id];
  if (!rune) return null;
  const rtypes = [rune.itype1, rune.itype2, rune.itype3, rune.itype4, rune.itype5, rune.itype6].filter(Boolean);
  const types = ['shld', 'tors', 'helm', 'weap'].filter(t => rtypes.some(rt => isSubType(rt, t) || (rt === 'pala' && t === 'shld')));
  type = type || (types.length === 1 && types[0]);
  let name = Locale.strings[rune.name];
if (type && types.length > 1) name = [name + ' ', <span key="type" className={`d2-color-${Color.GRAY}`}>({itemTypeNames[type]})</span>];
  return <ItemNode item={itemCreateTemplate(id, type)} type={type} text={text || name}/>;
}
function itemCraftedNode(text, id, base) {
  const recipe = Data.crafted[id];
  const item = Data.items[base];
  if (!recipe || !item) return null;
  if (!text) text = `${_L(craftingRecipes[recipe.description])} ${Locale.strings[item.namestr] || item.name}`;
  return <ItemNode item={itemCreateCrafted(id, base, true)} text={text}/>;
}

function itemLinkedNode(text, id) {
  if (presetItems[id]) return <ItemNode item={presetItems[id]} text={text}/>;
  if (Data.items[id]) return itemBaseNode(text, id);
  if (Data.uniqueItems[id] || Data.setItems[id]) return itemUniqueNode(text, id);
  const p = id.split('#');
  if (Data.runes[p[0]]) return itemRunewordNode(text, p[0], p[1]);
  if (Data.crafted[p[0]]) return itemCraftedNode(text, p[0], p[1]);
}

export function itemLookupId(text) {
  initLookup();
  const lookup = canonicalName(text);
  if (itemUniques[lookup]) {
    return itemUniques[lookup];
  } else if (itemPreset[lookup]) {
    return itemPreset[lookup];
  } else if (itemBases[lookup]) {
    return itemBases[lookup];
  }
}

export function itemTipNode(text, iid) {
  if (iid) return itemLinkedNode(text, iid);

  initLookup();
  let id;
  const lookup = canonicalName(text);
  if ((id = itemUniques[lookup])) {
    return itemUniqueNode(null, id);
  } else if ((id = itemPreset[lookup])) {
    return <ItemNode item={presetItems[id]} text={itemGetName(presetItems[id])}/>;
  } else if ((id = itemBases[lookup])) {
    return itemBaseNode(null, id);
  } else {
    const rw = parseRuneword(lookup);
    if (rw) return itemRunewordNode(null, ...rw);
    const recipe = parseRecipe(lookup);
    if (recipe) return itemCraftedNode(null, ...recipe);
  }
}

export function skillTipNode(text, slvl, sid) {
  if (!slvl) slvl = 0;
  if (sid) return <SkillNode id={sid} level={slvl} text={text}/>;
  initLookup();
  const id = skills[canonicalName(text)];
  if (id) return <SkillNode id={id} level={slvl}/>;
}

export function shrineTipNode(text, sid) {
  if (sid) return <ShrineNode id={sid} text={text}/>;
  initLookup();
  const id = shrines[canonicalName(text)];
  if (id) return <ShrineNode id={id}/>;
}

const SkillIconTip = ({ id, size }) => <D2SkillTooltip id={id} full canLevel="special"><SkillIcon id={id} size={size}/></D2SkillTooltip>;

export function skillIconNode(text, sid, size) {
  if (sid) return <SkillIconTip id={sid} size={size}/>;
  initLookup();
  const id = skills[canonicalName(text)];
  if (id) return <SkillIconTip id={id} size={size}/>;
}

export default function makeGameTips() {
  const store = new Store({ embed: true });
  const storeCache = {};
  function profileStore(id, state) {
    if (!storeCache[id]) {
      storeCache[id] = new Store({...state, embed: true});
    }
    return storeCache[id];
  }

  function render(elem, node, storeOverride) {
    if (elem) {
      ReactDOM.render((
        <Store.Provider store={storeOverride || store}>
          {elem}
        </Store.Provider>
      ), node);
    }
  }

  function createNode(node) {
    const text = node.textContent;
    const iid = node.getAttribute("data-d2planner-id");
    if (node.classList.contains("d2planner-item")) {
      const pid = node.getAttribute("data-d2planner-profile");
      if (pid) {
        loadProfile(pid).then(data => {
          const item = data?.items?.[iid];
          if (item) render(<ItemNode text={text} item={item}/>, node, profileStore(pid, data));
        }, () => null);
        return null;
      } else {
        return itemTipNode(text, iid);
      }
    } else if (node.classList.contains("d2planner-skill")) {
      let lvl = parseInt(node.getAttribute("data-d2planner-level") || '0');
      if (isNaN(lvl)) lvl = 0;
      return skillTipNode(text, lvl, iid);
    } else if (node.classList.contains("d2planner-shrine")) {
      return shrineTipNode(text, iid);
    } else if (node.classList.contains("d2planner-skill-icon")) {
      const pid = node.getAttribute("data-d2planner-profile");
      const pset = node.getAttribute("data-d2planner-set");
      const size = node.getAttribute("data-d2planner-size") || 48;
      const result = skillIconNode(text, iid, size);
      if (result && pid) {
        renderProfile(node, pid, pset, result).catch(() => null);
      } else {
        return result;
      }
    }
  }

  for (const node of document.querySelectorAll(".d2planner-item, .d2planner-skill, .d2planner-skill-icon, .d2planner-shrine")) {
    render(createNode(node), node);
  }
}
